import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Icon, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import ConfirmModal from 'modules/payroll_bundle/components/ConfirmModal'
import { usePayrollBundlePageContext } from 'modules/payroll_bundle/components/PayrollBundle'
import WorksheetTable from 'modules/payroll_bundle/components/common/components/WorksheetTable'
import {
  SupplementMatchDataType,
  matchData,
} from 'modules/payroll_bundle/components/common/helper/worksheet'
import { useMatchCycleSupplementsImportUrl } from 'modules/payroll_bundle/hooks/useMatchPayrollUrls'
import { useBulkImportSupplements } from 'modules/payroll_bundle/lib/bulkImportSupplementsContext'
import { BulkImportSupplementStage } from 'modules/payroll_bundle/lib/bulkImportSupplementsContext'
import {
  RESET_DATA,
  REVIEW_AND_IMPORT,
  SELECT_DATA,
  UPDATE_DATA,
  UPLOAD_FILE,
} from 'modules/payroll_bundle/lib/constants'
import EmployeeMatcher from 'modules/payroll_bundle/pages/SupplementsPage/BulkImport/_EmployeeMatcher'

import ConfirmImportModal from './Header/ConfirmImportModal'
import filterSelectedValidRows from './Header/filterSelectedValidRows'
import WorksheetHeader from './_components/WorksheetHeader'

const stageToPreviousStage: Record<BulkImportSupplementStage, BulkImportSupplementStage | null> = {
  UPLOAD_FILE: null, // There's no previous step
  SELECT_DATA: UPLOAD_FILE,
  REVIEW_AND_IMPORT: SELECT_DATA,
}

// Adapted from /modules/payroll_bundle/pages/ResultsAndPaymentsPage/ResultsPage/index.tsx
const BulkImport = () => {
  const [actions, setActions] = React.useState({
    primary: { label: '', onClick: () => {}, disabled: true },
    secondary: { label: '', onClick: () => {} },
  })

  const [showConfirmModal, setShowConfirmModal] = React.useState<boolean>(false)

  const [showConfirmImportModal, setShowConfirmImportModal] = React.useState(false)

  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const payrollBundleContext = usePayrollBundlePageContext()
  const { selectedPolicy, policyPeriod } = payrollBundleContext

  const isCycleSupplementsImportPage = useMatchCycleSupplementsImportUrl(
    selectedPolicy?.id,
    policyPeriod?.id
  )

  const [visibleButtons, setVisibleButtons] = React.useState<
    Array<Record<string, string | boolean>>
  >([
    {
      name: i18n.t('payroll_bundle.import_supplements.stage_two.name'),
      required: true,
    },
    {
      last_name: i18n.t('payroll_bundle.import_supplements.stage_two.last_name'),
      required: true,
    },
    {
      identifier: i18n.t('payroll_bundle.import_supplements.stage_two.identifier'),
    },
  ])

  const {
    matchedData,
    activeButton,
    worksheetData,
    supplementDispatch,
    worksheetMatches,
    bulkImportSupplementStage,
    supplementTypes,
    importedSupplements,
    selectedSupplements,
  } = useBulkImportSupplements()

  const selectedValidRows = useUnsafeMemo(() => {
    return filterSelectedValidRows({
      rows: importedSupplements,
      selectedSupplements,
      supplementTypes,
    })
  }, [importedSupplements, selectedSupplements, supplementTypes])

  const nonZeroSupplementsCount = useUnsafeMemo(() => {
    return selectedValidRows.reduce((count, row) => {
      return (
        count +
        supplementTypes.filter((supplementType) => {
          const amount = row[supplementType.id]
          if (typeof amount === 'string') {
            const trimmed = amount.trim()
            return !!trimmed && trimmed !== '0'
          }

          return !!amount
        }).length
      )
    }, 0)
  }, [selectedValidRows, supplementTypes])

  const handleBackClick = () => {
    setShowConfirmModal(true)
  }

  const handleBackCancel = () => {
    setShowConfirmModal(false)
  }

  const handleBackConfirm = () => {
    const previousStep = stageToPreviousStage[bulkImportSupplementStage]

    if (!previousStep || previousStep === UPLOAD_FILE) {
      supplementDispatch({
        type: RESET_DATA,
      })

      navigate(
        navigation.payrollBundle.policy.period.cycles.supplements.fullPath({
          policyId: selectedPolicy?.id,
          periodId: policyPeriod?.id,
        })
      )
      return
    }

    supplementDispatch({
      type: UPDATE_DATA,
      payload: {
        bulkImportSupplementStage: previousStep,
      },
    })

    setShowConfirmModal(false)
  }

  const hasAllRequiredMappings = () => {
    // Assumes the keys will be in the insertion order, but that won't work if
    // there are number-like keys
    // https://stackoverflow.com/a/5525820
    const requiredMappingsNames = visibleButtons
      .filter((b) => b.required)
      .map((b) => Object.keys(b)[0])
    return (
      requiredMappingsNames.every((mappingName) => mappingName && worksheetMatches[mappingName]) &&
      // There must be at least 1 supplement type too, and all of them must be mapped
      supplementTypes.length > 0 &&
      supplementTypes.map((supplementType) => supplementType.id).every((id) => worksheetMatches[id])
    )
  }

  const showReviewAndImportStep = () => {
    const data: SupplementMatchDataType[] = matchData({
      worksheetData,
      worksheetMatches,
      defaultMatchedData: { userId: null },
    }).filter((item): item is SupplementMatchDataType => 'userId' in item)

    supplementDispatch({
      type: UPDATE_DATA,
      payload: {
        bulkImportSupplementStage: REVIEW_AND_IMPORT,
        matchedData: data,
      },
    })
  }

  const hasRequiredMappings = bulkImportSupplementStage === SELECT_DATA && !hasAllRequiredMappings()
  const hasSupplementCount =
    bulkImportSupplementStage === REVIEW_AND_IMPORT && nonZeroSupplementsCount === 0

  const handlePrimaryClick = useUnsafeCallback(() => {
    if (bulkImportSupplementStage === REVIEW_AND_IMPORT) {
      setShowConfirmImportModal(true)
    } else {
      showReviewAndImportStep()
    }
  }, [bulkImportSupplementStage, showReviewAndImportStep])

  const transactionlActionsDerived = () => {
    const getPrimaryLabel = () => {
      return bulkImportSupplementStage === REVIEW_AND_IMPORT
        ? i18n.tp('payroll_bundle.import_supplements.review_and_import.import_supplements', {
            count: nonZeroSupplementsCount,
          })
        : i18n.t('payroll_bundle.import_supplements.upload.continue')
    }

    const getSecondaryLabel = () => {
      return bulkImportSupplementStage === REVIEW_AND_IMPORT
        ? i18n.t('common.back')
        : i18n.t('common.cancel')
    }

    const isPrimaryDisabled = hasRequiredMappings || hasSupplementCount

    return {
      primary: {
        label: getPrimaryLabel(),
        onClick: handlePrimaryClick,
        disabled: isPrimaryDisabled,
      },
      secondary: {
        label: getSecondaryLabel(),
        onClick: handleBackClick,
      },
    }
  }

  useUnsafeEffect(() => {
    const actionsInside = transactionlActionsDerived()
    setActions(actionsInside)
  }, [bulkImportSupplementStage, hasRequiredMappings, nonZeroSupplementsCount])

  useUnsafeEffect(() => {
    if (bulkImportSupplementStage === UPLOAD_FILE && isCycleSupplementsImportPage) {
      supplementDispatch({
        type: RESET_DATA,
      })

      navigate(
        navigation.payrollBundle.policy.period.cycles.supplements.fullPath({
          policyId: selectedPolicy?.id,
          periodId: policyPeriod?.id,
        })
      )
    }
  }, [bulkImportSupplementStage])

  useUnsafeEffect(() => {
    return () => {
      if (isCycleSupplementsImportPage) {
        supplementDispatch({
          type: RESET_DATA,
        })
      }
    }
  }, [])

  const workSheetInformation = {
    [SELECT_DATA]: {
      title: i18n.t('payroll_bundle.import_supplements.step_info', {
        page: 1,
        title: i18n.t('payroll_bundle.import_supplements.upload.worksheet_title'),
      }),
      sub_title: i18n.t('payroll_bundle.import_supplements.upload.worksheet_sub_title'),
    },
    [REVIEW_AND_IMPORT]: {
      title: i18n.t('payroll_bundle.import_supplements.step_info', {
        page: 2,
        title: i18n.t('payroll_bundle.import_supplements.review_and_import.title'),
      }),
      sub_title: i18n.t('payroll_bundle.import_supplements.review_and_import.subtitle'),
    },
  } as const

  const currentStage = useUnsafeCallback(() => {
    switch (bulkImportSupplementStage) {
      case SELECT_DATA: {
        return (
          <WorksheetTable
            padding="s0"
            dispatch={supplementDispatch}
            activeButton={activeButton}
            worksheetData={worksheetData}
            worksheetMatches={worksheetMatches}
            header={
              <WorksheetHeader
                visibleButtons={visibleButtons}
                setVisibleButtons={setVisibleButtons}
              />
            }
          />
        )
      }

      case REVIEW_AND_IMPORT: {
        return <EmployeeMatcher matchedData={matchedData} />
      }
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [bulkImportSupplementStage, worksheetData, activeButton, worksheetMatches])

  return (
    <ResponsivePage.Body showHeader={false} variant="full" transactionalActions={actions}>
      <Box width="full">
        <Box gap="s16">
          <Box flexDirection="row" gap="s4" alignItems="center">
            <Icon.Large icon="common-file-text-upload" />
            <Text.Header2>
              {
                workSheetInformation[bulkImportSupplementStage as keyof typeof workSheetInformation]
                  ?.title
              }
            </Text.Header2>
          </Box>

          <Text.Normal>
            {
              workSheetInformation[bulkImportSupplementStage as keyof typeof workSheetInformation]
                ?.sub_title
            }
          </Text.Normal>
        </Box>
        {currentStage()}
      </Box>
      {showConfirmImportModal && (
        <ConfirmImportModal
          nonZeroSupplementsCount={nonZeroSupplementsCount}
          onClose={() => setShowConfirmImportModal(false)}
        />
      )}
      {showConfirmModal && (
        <ConfirmModal
          title={i18n.t('payroll_bundle.import_supplements.back_modal.title')}
          onCancel={handleBackCancel}
          onConfirm={handleBackConfirm}
        />
      )}
    </ResponsivePage.Body>
  )
}

export default observer(BulkImport)
